/* ──────────────────────────────────────────────────────────────────
   Plug Header — interactions

   The section/blocks server-render the desktop nav (L1 buttons +
   mega panels with L2 cards). This script:
     - wires hover/focus to open/close mega panels
     - toggles the hamburger between menu/X icons as the drawer
       opens and closes (per design brief: hamburger reverts to X
       when drawer is open; clicking X closes and swaps back)
     - toggles the mobile search row open/closed (same icon-swap idea)
     - builds the mobile drawer rows and drilldown subpanels by
       reading the server-rendered .pgs-nav-item nodes, so L1/L2
       data lives in one place
   ────────────────────────────────────────────────────────────────── */

(function () {
  'use strict';

  /* Minimal inline SVG strings for icons that need to swap or be
     injected at runtime. The Liquid snippet ships the initial icons;
     these strings are only used when JS replaces them. */
  const SVG = {
    menu:
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M3 6h18M3 12h18M3 18h18"/></svg>',
    close:
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>',
    search:
      '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>',
    chevLeft:
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="transform:rotate(90deg)" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>',
    arrow:
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
  };

  function h(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
  }

  /* ──────────  Mega menu (desktop) hover/focus  ────────── */
  function wireMegaMenu(root) {
    const navList = root.querySelector('.pgs-nav-list');
    if (!navList) return;

    let openItem = null;
    let closeTimer = null;

    function openMega(item) {
      clearTimeout(closeTimer);
      if (openItem && openItem !== item) closeNow(openItem);
      const mega = item.querySelector('.pgs-mega');
      if (!mega) return; // link-only L1
      item.classList.add('is-open');
      // Trigger reflow so the transition runs on first open.
      mega.getBoundingClientRect();
      mega.classList.add('is-open');
      openItem = item;
    }

    function closeNow(item) {
      const mega = item.querySelector('.pgs-mega');
      item.classList.remove('is-open');
      if (mega) mega.classList.remove('is-open');
      if (openItem === item) openItem = null;
    }

    function scheduleClose() {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (openItem) closeNow(openItem);
      }, 140);
    }

    navList.addEventListener(
      'mouseenter',
      (e) => {
        const item = e.target.closest('.pgs-nav-item');
        if (!item || !navList.contains(item)) return;
        if (item.querySelector('.pgs-mega')) openMega(item);
        else scheduleClose();
      },
      true,
    );

    navList.addEventListener('focusin', (e) => {
      const item = e.target.closest('.pgs-nav-item');
      if (item && item.querySelector('.pgs-mega')) openMega(item);
    });

    const nav = root.querySelector('.pgs-nav');
    if (nav) {
      nav.addEventListener('mouseleave', scheduleClose);
      nav.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    }
  }

  /* ──────────  Mobile drawer build (L1 rows + drilldowns)  ────────── */
  function buildDrawer(root) {
    const main = root.querySelector('[data-plug-header-drawer-main]');
    const tabs = root.querySelector('[data-plug-header-drawer-tabs]');
    if (!main || !tabs) return;

    main.innerHTML = '';
    tabs.innerHTML = '';

    const items = root.querySelectorAll('.pgs-nav-item');
    items.forEach((item, idx) => {
      const label = item.dataset.l1Label || '';
      const url = item.dataset.l1Url || '#';
      const type = item.dataset.l1Type || 'link';
      const tabId = 'plug-header-tab-' + idx;

      const li = document.createElement('li');
      if (type === 'mega') {
        li.appendChild(
          h(
            `<button type="button" class="pgs-drawer-row has-children" data-plug-header-drawer-open="${tabId}">
               <span>${escapeHtml(label)}</span>
             </button>`,
          ),
        );
      } else {
        li.appendChild(
          h(
            `<a class="pgs-drawer-row" href="${escapeAttr(url)}">
               <span>${escapeHtml(label)}</span>
             </a>`,
          ),
        );
      }
      main.appendChild(li);

      if (type !== 'mega') return;

      // Build the drilldown subpanel for this mega L1 by cloning data
      // from the server-rendered .pgs-cat cards inside its mega panel.
      const rows = Array.from(item.querySelectorAll('.pgs-mega .pgs-cat'))
        .map((cat) => {
          const href = cat.getAttribute('href') || '#';
          const img = cat.querySelector('.pgs-cat-icon img');
          const title = textOf(cat.querySelector('.pgs-cat-title'));
          const desc = textOf(cat.querySelector('.pgs-cat-desc'));
          const iconHtml = img
            ? `<img src="${escapeAttr(img.getAttribute('src'))}" alt="" loading="lazy">`
            : '';
          return `
            <li><a href="${escapeAttr(href)}" class="pgs-cat-row">
              <span class="pgs-cat-icon-sm">${iconHtml}</span>
              <span class="text">
                <span class="title">${escapeHtml(title)}</span>
                ${desc ? `<span class="desc">${escapeHtml(desc)}</span>` : ''}
              </span>
            </a></li>`;
        })
        .join('');

      // Shop-all CTA — mirror the desktop one if present. Copy the inline
      // style (CSS vars from the L1 block's shop-all color settings) so the
      // drawer button picks up the same overrides as the megamenu version.
      const shopAll = item.querySelector('.pgs-shopall');
      const shopAllHref = shopAll ? shopAll.getAttribute('href') || '#' : '#';
      const shopAllLabel = shopAll ? textOf(shopAll) : `SHOP ALL ${label.toUpperCase()}`;
      const shopAllStyle = shopAll ? shopAll.getAttribute('style') || '' : '';

      const panel = h(`
        <div class="pgs-drawer-tab" data-plug-header-tab-id="${tabId}">
          <div class="pgs-drawer-tab-head">
            <button type="button" class="back" data-plug-header-drawer-back>
              ${SVG.chevLeft} All categories
            </button>
          </div>
          <ul class="pgs-drawer-list" style="padding-top:8px">${rows}</ul>
          <a href="${escapeAttr(shopAllHref)}" class="pgs-drawer-shopall-btn pgs-no-inherit-color"${shopAllStyle ? ` style="${escapeAttr(shopAllStyle)}"` : ''}>
            ${escapeHtml(shopAllLabel)} ${SVG.arrow}
          </a>
        </div>
      `);
      tabs.appendChild(panel);
    });

    // Drilldown open/back delegation
    const drawer = root.querySelector('[data-plug-header-drawer]');
    if (!drawer) return;
    drawer.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-plug-header-drawer-open]');
      if (opener) {
        const tabId = opener.getAttribute('data-plug-header-drawer-open');
        const panel = drawer.querySelector(
          `.pgs-drawer-tab[data-plug-header-tab-id="${tabId}"]`,
        );
        if (panel) panel.classList.add('is-open');
        return;
      }
      const back = e.target.closest('[data-plug-header-drawer-back]');
      if (back) {
        const panel = back.closest('.pgs-drawer-tab');
        if (panel) panel.classList.remove('is-open');
      }
    });
  }

  /* ──────────  Drawer open/close + hamburger ↔ X toggle  ────────── */
  function wireDrawer(root) {
    const drawer = root.querySelector('[data-plug-header-drawer]');
    const backdrop = root.querySelector('[data-plug-header-backdrop]');
    const hamburger = root.querySelector('[data-plug-header-hamburger]');
    const hamburgerIcon = root.querySelector('[data-plug-header-hamburger-icon]');
    if (!drawer || !backdrop || !hamburger || !hamburgerIcon) return;

    function setOpen(on) {
      // document.body.classList.add("header-drawer-is-open")
      document.documentElement.classList.add("header-drawer-is-open")
      drawer.classList.toggle('is-open', on);
      backdrop.classList.toggle('is-on', on);
      drawer.setAttribute('aria-hidden', on ? 'false' : 'true');
      hamburger.setAttribute('aria-expanded', on ? 'true' : 'false');
      hamburger.setAttribute('aria-label', on ? 'Close menu' : 'Open menu');
      hamburgerIcon.innerHTML = on ? SVG.close : SVG.menu;
      if (!on) {
        // document.body.classList.remove("header-drawer-is-open");
        document.documentElement.classList.remove("header-drawer-is-open")
        // Reset any open drilldown after the close transition finishes.
        setTimeout(() => {
          drawer
            .querySelectorAll('.pgs-drawer-tab.is-open')
            .forEach((t) => t.classList.remove('is-open'));
        }, 320);
      }
    }

    // The hamburger button toggles: open if closed, close if open
    // (design brief — clicking the X reverts to hamburger and closes).
    hamburger.addEventListener('click', () => {
      const isOpen = drawer.classList.contains('is-open');
      setOpen(!isOpen);
    });

    backdrop.addEventListener('click', () => setOpen(false));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        setOpen(false);
      }
    });
  }

  /* ──────────  Drawer sizing (mobile/tablet)  ──────────
     CSS can't express "fill from .pgs-header's bottom edge to the viewport
     bottom" reliably — .pgs-header-root sits below an announcement bar that
     can resize, and the inline mobile search row expands the header itself.
     Measure .pgs-header on init, on header size changes (ResizeObserver),
     and on viewport changes (resize / orientationchange) and set the
     drawer's height + max-height inline. */
  function wireDrawerSizing(root) {
    const drawer = root.querySelector('[data-plug-header-drawer]');
    const headerEl = root.querySelector('.pgs-header');
    if (!drawer || !headerEl) return;

    let timer;
    let lastWidth = window.innerWidth;
    function update() {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // if (window.innerWidth !== lastWidth) {
        //   lastWidth = window.innerWidth;
        // }
        // const headerGroup = document.getElementById("header-group");
        // const headerGroupHeight = headerGroup.getBoundingClientRect().height;
        // document.documentElement.style.setProperty("--header-group-height", `${headerGroupHeight}px`);
        const headerBottom = headerEl.getBoundingClientRect().bottom;
        const available = Math.max(0, window.innerHeight - headerBottom);
        drawer.style.height = available + 'px';
        drawer.style.maxHeight = available + 'px';
      }, 400);
    }

    update();

    if (typeof ResizeObserver !== 'undefined') {
      new ResizeObserver(update).observe(headerEl);
    }
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
  }

  /* ──────────  Flex AJAX cart bootstrap  ──────────
     The ajax-cart snippet only adds the `jsAjaxCart` class on its
     wrapper when `context == 'mobile-header'`. Flex's section-init
     loop in app.js looks for `.shopify-section[class*=js]` to call
     `window.PXUTheme[class].init($section)` — so the global render
     from theme.liquid (`{% render 'ajax-cart' %}`) never triggers
     init. The old Flex headers worked because they included
     mobile-header.liquid; plug-header replaced that. We call init
     ourselves so the cart-icon click handler (delegated on
     `[data-ajax-cart-trigger]`) gets wired up. */
  function initFlexAjaxCart() {
    if (!window.PXUTheme || !window.PXUTheme.jsAjaxCart) return;
    if (typeof window.PXUTheme.jsAjaxCart.init !== 'function') return;
    const $ = window.jQuery;
    if (!$) return;
    const $section = $('#theme-ajax-cart');
    if (!$section.length || $section.data('plugInit')) return;
    $section.data('plugInit', true);
    window.PXUTheme.jsAjaxCart.init($section);
  }

  /* ──────────  Search autocomplete (predictive search)  ──────────
     Hits Shopify's native /search/suggest.json endpoint and renders
     product + query suggestions below each .pgs-search input. Both
     the desktop/tablet inline search and the mobile slide-down search
     get their own dropdown. */
  function wireSearchAutocomplete(root) {
    const forms = root.querySelectorAll('.pgs-search');
    forms.forEach((form) => {
      const input = form.querySelector('input[name="q"]');
      const dropdown = form.querySelector('[data-plug-header-search-results]');
      if (!input || !dropdown) return;

      let debounceTimer = 0;
      let controller = null;
      let lastQuery = '';

      function open() {
        dropdown.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
      function close() {
        dropdown.hidden = true;
        dropdown.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
      }

      function fmtPrice(p) {
        const n = parseFloat(p);
        return isNaN(n) ? '' : '$' + n.toFixed(2);
      }

      function render(query, data) {
        const results = (data && data.resources && data.resources.results) || {};
        const products = results.products || [];
        const queries = results.queries || [];

        if (!products.length && !queries.length) {
          dropdown.innerHTML =
            `<div class="pgs-search-empty">No results for "${escapeHtml(query)}"</div>`;
          open();
          return;
        }

        let html = '';

        if (queries.length) {
          html += '<div class="pgs-search-section">';
          queries.forEach((q) => {
            const text = q.text || q.title || '';
            const url = q.url || ('/search?q=' + encodeURIComponent(text));
            html +=
              `<a href="${escapeAttr(url)}" class="pgs-search-suggest" role="option">` +
                SVG.search +
                `<span>${escapeHtml(text)}</span>` +
              `</a>`;
          });
          html += '</div>';
        }

        if (products.length) {
          html += '<div class="pgs-search-section">';
          products.forEach((p) => {
            const img =
              (p.featured_image && p.featured_image.url) ||
              p.image ||
              '';
            const imgHtml = img
              ? `<img class="pgs-search-result-img" src="${escapeAttr(img)}" alt="" loading="lazy" width="44" height="44">`
              : `<span class="pgs-search-result-img"></span>`;
            const price = fmtPrice(p.price);
            html +=
              `<a href="${escapeAttr(p.url)}" class="pgs-search-result" role="option">` +
                imgHtml +
                `<div class="pgs-search-result-text">` +
                  `<div class="pgs-search-result-title">${escapeHtml(p.title || '')}</div>` +
                  (price ? `<div class="pgs-search-result-price">${escapeHtml(price)}</div>` : '') +
                `</div>` +
              `</a>`;
          });
          html += '</div>';
        }

        html +=
          `<a href="/search?q=${encodeURIComponent(query)}" class="pgs-search-view-all">` +
            `View all results for "${escapeHtml(query)}"` +
          `</a>`;

        dropdown.innerHTML = html;
        open();
      }

      function fetchResults(query) {
        if (controller) controller.abort();
        controller = new AbortController();
        const url =
          '/search/suggest.json' +
          '?q=' + encodeURIComponent(query) +
          '&resources[type]=product,query' +
          '&resources[limit]=6' +
          '&resources[options][unavailable_products]=last';

        fetch(url, { signal: controller.signal })
          .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
          .then((data) => {
            // Discard stale responses if user has kept typing.
            if (query !== lastQuery) return;
            render(query, data);
          })
          .catch((err) => {
            if (err && err.name === 'AbortError') return;
            // Silent failure — leave dropdown closed.
          });
      }

      function onInput() {
        const q = input.value.trim();
        lastQuery = q;
        clearTimeout(debounceTimer);
        if (!q) {
          close();
          return;
        }
        debounceTimer = setTimeout(() => fetchResults(q), 200);
      }

      input.addEventListener('input', onInput);
      input.addEventListener('focus', () => {
        if (input.value.trim()) onInput();
      });

      input.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          close();
          input.blur();
        }
      });

      // Click outside the form closes the dropdown.
      document.addEventListener('click', (e) => {
        if (!form.contains(e.target)) close();
      });
    });
  }

  /* ──────────  Mobile search expand/collapse  ────────── */
  function wireMobileSearch(root) {
    const headerInner = root.querySelector('.pgs-header-inner');
    const toggle = root.querySelector('[data-plug-header-mobile-search-toggle]');
    const toggleIcon = root.querySelector('[data-plug-header-mobile-search-icon]');
    if (!headerInner || !toggle || !toggleIcon) return;

    function setOpen(on) {
      headerInner.dataset.searchOpen = on ? 'true' : 'false';
      toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
      toggleIcon.innerHTML = on ? SVG.close : SVG.search;
      if (on) {
        const input = root.querySelector('.pgs-mobile-search-row input');
        if (input) setTimeout(() => input.focus(), 250);
      }
    }

    toggle.addEventListener('click', () => {
      setOpen(headerInner.dataset.searchOpen !== 'true');
    });
  }

  /* ──────────  Bootstrap  ────────── */
  function init(root) {
    if (!root || root.dataset.plugHeaderInit === '1') return;
    root.dataset.plugHeaderInit = '1';
    wireMegaMenu(root);
    buildDrawer(root);
    wireDrawer(root);
    wireDrawerSizing(root);
    wireMobileSearch(root);
    wireSearchAutocomplete(root);
    initFlexAjaxCart();
  }

  function initAll() {
    document.querySelectorAll('[data-pgs-header]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when the theme editor reloads the section so the drawer
  // gets rebuilt from the updated server-rendered nav.
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-pgs-header]');
    if (root) {
      root.dataset.plugHeaderInit = '';
      init(root);
    }
  });

  /* ──────────  Helpers  ────────── */
  function textOf(el) {
    return el ? (el.textContent || '').trim() : '';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
    );
  }
  function escapeAttr(s) {
    return escapeHtml(s);
  }
})();
